import LCS_GENERATED_CARDS from "../data/lcsGeneratedCards.json";
import LCS_LIVE_AGE_DAYS   from "../data/lcsLeaseAgeDays.json";
import { deriveLedger } from "../services/lcsRecommendationLedger";

/* ───────────────────────────────────────────────────────────────────────────
 * lcsDecisionProfiles.js — per-lease decision profiles for the LCS cockpit.
 *
 * Two layers ship from this module:
 *
 *   (A) LCS_DECISION_PROFILES — hand-curated narrative profiles, balanced
 *       ~40% field notes / 30% production / 20% econ / 10% confidence.
 *       The note quote lives in `evidence` (single source). The integrated
 *       diagnosis lives in `whyItMatters` (note + production + econ +
 *       confidence in 2-3 sentences).
 *
 *   (B) Decision Inference Engine (data-grounded) — deterministic, pure-JS
 *       scoring on top of the curated narrative. Adds:
 *         - priorityScore (0-100) + priorityLabel
 *         - economicImpact { type, range, confidence }
 *         - dataFreshness { status, ageDays, warning }
 *         - keyDrivers (3 signal bullets)
 *       Driven by LCS_LEASE_METRICS (baked from data/lcs_decision_set_probe.json)
 *       and the LCS_ECON_DECK heuristic. Live overlay_staged payloads override
 *       the baked metrics when available.
 *
 * Resolution order in the cockpit (see resolveDecisionProfile):
 *   1. Fresh overlay_staged include_llm payload (when present, narrative only)
 *   2. LCS_DECISION_PROFILES + computed inference (this file)
 *   3. null — caller falls back to inferLeaseCommentary
 *
 * Field names preserved for the right-rail renderer in
 * ForecastDashboardDecisionUI.js; new inference fields are additive.
 * ──────────────────────────────────────────────────────────────────────── */

/* ── Econ deck (heuristic) ────────────────────────────────────────────────
 * Used by the inference engine to estimate monthly $ impact ranges. These
 * are intentionally simple and DO NOT replace the EconModal cost deck —
 * they're the same order of magnitude but should be reconciled with
 * EconModal config in a follow-up. Keep here as a single source of truth
 * for inference-engine $ ranges.
 * ─────────────────────────────────────────────────────────────────────── */
export const LCS_ECON_DECK = {
  oilPricePerBbl: 70,        // $/bbl
  gasPricePerMcf: 3,         // $/mcf
  waterDisposalLow: 1,       // $/bbl SWD low end
  waterDisposalHigh: 3,      // $/bbl SWD high end
  daysPerMonth: 30,
};

export const LCS_DECISION_PROFILES_SCHEMA = {
  leaseId: "string",
  leaseName: "string",
  primaryAction:
    "Optimize Production | Monitor | Investigate Decline | Review LOE | " +
    "Water Burden Review | Gas Anomaly Review | Shut-in Candidate | " +
    "Data Quality Review | Investigate Gas Handling | " +
    "Post-Workover Watch | Post-Rig Stabilization Watch | " +
    "Verify Line Integrity",
  priority: "High | Medium | Low",
  confidence: "High | Medium | Low",
  shortThesis: "string — one diagnostic sentence (note+prod+econ in one breath)",
  whyItMatters:
    "string — integrated diagnosis: note evidence + production confirmation + econ implication + confidence/data-quality (2–3 sentences)",
  productionSignal: "string — pure production: rates, trend, volatility, MAPE",
  econSignal:
    "string — pure economic implication: which dollar lever matters; honest about cost-deck uncertainty",
  recommendedActions: ["3 field-actionable Monday-morning steps"],
  riskFlags: ["3–4 compact tags (ops + data-quality)"],
  evidence: ["bullets — production numerics + ONE key note quote"],
  operatorTone: "string — short field-voice line",
};

export const LCS_DECISION_PROFILES = {
  // ─────────────────────────────────────────────────────────────────────────
  // Rewritten 2026-05-03 from fresh overlay_staged.php data
  // (scripts/reprofile_lcs_decisions.mjs, no token/grant, cache-busted).
  // Balance: ~40% notes / 30% production / 20% econ / 10% confidence.
  // ─────────────────────────────────────────────────────────────────────────

  // ── CARTERBF ─────────────────────────────────────────────────────────────
  CARTERBF: {
    leaseId: "CARTERBF",
    leaseName: "CARTER, B.F.",
    primaryAction: "Optimize Production",
    priority: "Medium",
    confidence: "High",
    shortThesis:
      "March separator-handling diagnosis appears resolved in fresh data: gas trend reversed -22% to +1%, gas MAPE dropped from 65% to 12%, water p95 down from 393 to 217 bbl/d. Lease is now a stable producer; water optimization is the live lever.",
    whyItMatters:
      "The 03-07 pumper note about gas line at 57 psi and two-phase swamped pointed to a real surface-handling defect. Fresh production data shows the fix held: gas band is tight at 41 mcf/d with MAPE down to 12%, oil is steady at ~27 bbl/d, water exposure has roughly halved. Forecast is now 0d (fresh, today), so dollar sizing is back on the table — but water MAPE is still 300% so any SWD move should be staged not committed.",
    productionSignal:
      "Oil p50 26.7 / p95 41.8 bbl/d (194d, MAPE 9%, trend -5% 7v30 — modest pullback, within model). Gas p50 41 / p95 46 mcf/d (107d, MAPE 12% — clean, was 65%); trend +1%. Water p50 28 / p95 217 bbl/d (91d, MAPE 300% — still volatile).",
    econSignal:
      "Margin attractive and now defensible at default deck. Water-disposal exposure has materially improved (p95 393 → 217); SWD contract review and chemical/pump tuning are reasonable next moves. Directional uplift sized off recent monthly oil + gas revenue.",
    recommendedActions: [
      "Verify the 03-07 separator/back-pressure fix is documented as a permanent change (not a temporary patch)",
      "Pull last 60 days of water-disposal tickets — sized SWD lever now defensible at p95 217 bbl/d",
      "Audit chemical program against current oil rate; trim if over-spec",
    ],
    riskFlags: [
      "Water MAPE 300%",
      "Recent oil trend -5% (watch)",
      "p95 water still ~8x p50 — episodic surface events",
      "SWD pricing not yet reconciled to deck",
    ],
    evidence: [
      "Oil p50/p95 = 26.7 / 41.8 bbl/d (194d, MAPE 9%)",
      "Gas p50/p95 = 41 / 46 mcf/d (107d, MAPE 12% — was 65%); trend_pct_7v30 = +1%",
      "Water p50/p95 = 28 / 217 bbl/d (91d, MAPE 300% — was p95 393)",
      "Note 2026-03-07 (historical): gas line 57 psi, two-phase swamped, knockout same pressure — fix appears to have held",
    ],
    operatorTone:
      "March surface-handling call was right. Fix held. Now it is a clean producer — water and chemical are the next dollar moves.",
  },

  // ── CHADR288 ─────────────────────────────────────────────────────────────
  CHADR288: {
    leaseId: "CHADR288",
    leaseName: "CHADBOURNE RANCH -288-",
    primaryAction: "Investigate Decline",
    priority: "High",
    confidence: "High",
    shortThesis:
      "Three consecutive days of 'Meter is asleep' (05-01, 05-02, 05-03) — measurement is dropping out daily; oil and gas numbers may understate actual production until the meter is back.",
    whyItMatters:
      "The most recent three notes all say the same thing: meter asleep on consecutive days through today (2026-05-03). That means recent oil and gas readings may be artifacts of intermittent measurement, not steady production. Oil model has tightened (MAPE 8%) and gas is clean (MAPE 9%, +14% trend), but every recent number is suspect until the meter is restored. Don't celebrate the trend lift — confirm the data first.",
    productionSignal:
      "Oil p50 9.2 / p95 13.9 bbl/d (91d, MAPE 8%, trend -4% 7v30 — but suspect). Gas p50 32 / p95 35 mcf/d (81d, MAPE 9%, trend +14% — also suspect under intermittent measurement). Water sparse (4 positives, p50 3 bbl/d).",
    econSignal:
      "Cannot size dollars off this window. The +14% gas trend would be a clear upside signal IF the meter were live, but with three consecutive 'asleep' days the recent values may be drop-and-recover artifacts. Resolve the meter before any optimization conversation.",
    recommendedActions: [
      "Dispatch tech to inspect/repair the meter today — three consecutive 'asleep' days is an active equipment issue, not paperwork",
      "Once meter is live, give it 7 days of clean data, then re-evaluate the gas +14% trend on its own merits",
      "Hold any LOE/optimization decision until measurement is restored",
    ],
    riskFlags: [
      "Meter asleep 05-01/02/03 (3 consecutive days)",
      "Recent rates may be measurement artifacts",
      "Water sparse (4 positives)",
      "Trend signals unreliable until meter restored",
    ],
    evidence: [
      "Oil p50/p95 = 9.2 / 13.9 bbl/d (91d, MAPE 8% — was 76%)",
      "Gas p50/p95 = 32 / 35 mcf/d (81d, MAPE 9%); trend_pct_7v30 = +14%",
      "Notes 05-01, 05-02, 05-03: 'Meter is asleep' — three consecutive days",
      "Earlier notes 02-13 → 04-15: routine haul/load tickets",
    ],
    operatorTone:
      "Three days of 'meter is asleep' is the only signal that matters right now. Get the meter back, then read the numbers.",
  },

  // ── GILLUMA ──────────────────────────────────────────────────────────────
  GILLUMA: {
    leaseId: "GILLUMA",
    leaseName: "GILLUM A",
    primaryAction: "Verify Line Integrity",
    priority: "High",
    confidence: "High",
    shortThesis:
      "2R flow-line leak logged THREE consecutive days (04-27, 04-28, 04-29) — recurring, not a one-off. Forecast is fresh; the integrity-review case stands on the field-note pattern alone.",
    whyItMatters:
      "Three same-well notes on consecutive days is no longer a single repair event — it's a pattern that says the line is actively failing. Oil model is tight (MAPE 8%), but it includes the pre-event data. Pause optimization spend until the line is proven stable; if a fourth event logs, escalate to formal workover.",
    productionSignal:
      "Oil p50 9.3 / p95 11.6 bbl/d (92d, MAPE 8% — clean). Gas inactive. Water p50 50 / p95 140 bbl/d (41d, MAPE 89%, trend +32% — consistent with leak events).",
    econSignal:
      "Cannot size dollars on a recurring integrity event. Cost of escalating to integrity review now is trivial vs. cost of optimizing a well that may need a tubing/flow-line workover.",
    recommendedActions: [
      "Escalate 2R to integrity review — three consecutive-day leak events is a pattern, not a repair",
      "Watch for a fourth event over the next 5–7 days — if it logs, formal workover decision",
      "Hold all optimization spend until 7 leak-free days post-04-29",
    ],
    riskFlags: [
      "2R flow-line leaks 04-27/28/29 (three consecutive days)",
      "Water trend +32%, MAPE 89%",
      "Pre-event oil model may understate post-event baseline",
      "Workover risk if pattern continues",
    ],
    evidence: [
      "Oil p50/p95 = 9.3 / 11.6 bbl/d (92d, MAPE 8%)",
      "Gas: 0 positive days",
      "Water p50/p95 = 50 / 140 bbl/d (41d, MAPE 89%); trend_pct_7v30 = +32%",
      "Notes 04-27, 04-28, 04-29: '2R off due to flow line leak. Back on' (three consecutive days)",
    ],
    operatorTone:
      "Three days of the same leak is not a repair — it is a pattern. Escalate, watch for a fourth.",
  },

  // ── NORTHW ───────────────────────────────────────────────────────────────
  NORTHW: {
    leaseId: "NORTHW",
    leaseName: "NORTH WICKHAM",
    primaryAction: "Investigate Decline",
    priority: "High",
    confidence: "High",
    shortThesis:
      "Belt failures on Wickham #2 are now recurring without rain context — four consecutive days (04-30, 05-01, 05-02, 05-03) of 'Belts burn #2'. This is a mechanical pattern, not weather. Highest-rate producer in the set is being systematically downgraded.",
    whyItMatters:
      "April had 'Belts burnt off due to rain #2 and #9' (04-18/19) followed by recovery. But the latest four days log 'Belts burn #2' DAILY without rain attribution — same well failing repeatedly. Oil rate has slipped to p50 56 (down from earlier 57), water trend is -76% 7v30 (consistent with downtime suppressing produced water). On top of that the Tank-01 carryover from Wickham #3 still complicates per-lease attribution. The action is mechanical investigation, not optimization.",
    productionSignal:
      "Oil p50 56.0 / p95 68.5 bbl/d (92d, MAPE 21%, trend -24% 7v30 — downtime-driven). Gas inactive. Water p50 33 / p95 274 bbl/d (46d, MAPE 164%, trend -76% — confirming Well #2 downtime suppressing produced volume).",
    econSignal:
      "Still the highest-cashflow producer in the set on a per-day basis, but realized days are being lost to belt failures. Action is uptime restoration on #2, not LOE/SWD spend on the lease overall. Each restored day at p50 ~56 bbl/d × $70 = ~$3.9k oil revenue recovered.",
    recommendedActions: [
      "Pull Wickham #2 belt assembly and inspect drive train — four consecutive failures without rain points to alignment, sheave wear, or undersized belts",
      "While #2 is down, reconcile Tank 01 with Wickham #3 to verify per-lease attribution stays clean",
      "Once #2 is back stable for 7+ days, NORTHW returns to highest-cashflow optimization candidate — verify SWD price first",
    ],
    riskFlags: [
      "Belts burn #2 daily 04-30 → 05-03 (4 consecutive days, no rain context)",
      "Oil trend -24% (downtime-driven)",
      "Water MAPE 164%, attribution caveat",
      "Tank 01 carryover from Wickham #3",
    ],
    evidence: [
      "Oil p50/p95 = 56.0 / 68.5 bbl/d (92d, MAPE 21%)",
      "Gas: 0 positive days",
      "Water p50/p95 = 33 / 274 bbl/d (46d, MAPE 164%); trend -76% 7v30",
      "Notes 04-30, 05-01, 05-02, 05-03: 'Belts burn #2' (four consecutive days, no rain)",
      "Notes 04-18/19: 'Belts burnt off due to rain #2 and #9' (earlier rain-attributed event)",
      "Note 02-08: 'Tank 01 is carried over from Wickham # 3 tank' — cross-lease attribution caveat",
    ],
    operatorTone:
      "Four straight days of belts burning on #2 with no rain. Pull the assembly and look at the sheave. This is mechanical, not weather.",
  },

  // ── CRICKETSU ────────────────────────────────────────────────────────────
  CRICKETSU: {
    leaseId: "CRICKETSU",
    leaseName: "CRICKET (STRAWN) UNIT",
    primaryAction: "Data Quality Review",
    priority: "Medium",
    confidence: "High",
    shortThesis:
      "Injection line was repaired 04-30 with planned weekend switch back to #6 — the data-quality emergency is resolving. Trend now -2% (down from +79%), consistent with contamination spike unwinding. Re-evaluate in ~2 weeks of clean post-repair operation.",
    whyItMatters:
      "Earlier 27-note narrative was correct — tubing leak on #9, multiple SWD reroutes, water on oil tank caused the false 3× spike. Pumper note 04-30: 'Injection line has been repaired. I'll have water switched back into #6 Saturday or Sunday'. The +79% trend has fully unwound to -2% as the contamination signal clears. Forecast is now fresh (0d), but water MAPE is still 182% so don't trust the absolute level yet.",
    productionSignal:
      "Oil p50 11.7 / p95 28.4 bbl/d (87d, MAPE 28%, trend -2% 7v30 — was +79%, contamination unwinding). Gas inactive. Water p50 16 / p95 67 bbl/d (46d, MAPE 182%, trend -37%).",
    econSignal:
      "Forecast no longer contaminated by spike, but water MAPE 182% still says don't size SWD precisely. The dollar lever returns once the SWD switch back to #6 is confirmed and 14 days of clean reads accumulate.",
    recommendedActions: [
      "Confirm SWD switched back to #6 over the weekend per the 04-30 plan; log the switch date for forecast attribution",
      "Hold optimization decisions ~14 days post-switchback to let water MAPE tighten",
      "Once stable, this returns to a normal optimization candidate at ~12 bbl/d oil",
    ],
    riskFlags: [
      "Water MAPE 182% (still volatile)",
      "Tank attribution recently reset by SWD reroutes",
      "Repair pending verification of #6 switchback",
      "Spike contamination unwound but model needs clean window",
    ],
    evidence: [
      "Oil p50/p95 = 11.7 / 28.4 bbl/d (87d, MAPE 28%); trend_pct_7v30 = -2% (was +79%)",
      "Gas: 0 positive days",
      "Water p50/p95 = 16 / 67 bbl/d (46d, MAPE 182%); trend -37% (contamination unwinding)",
      "Note 04-30: 'Injection line has been repaired. I'll have water switched back into #6 Saturday or Sunday'",
      "Note 04-28: 'Injection line joint was replaced. SWD has been switched back into #6'",
      "Note 04-15 (historical): 'HAD MATHIS PULL WATER OFF OF OIL TANK' — explained the earlier spike",
    ],
    operatorTone:
      "Surface mess is mostly cleaned up. Confirm the weekend switchback to #6, give it two weeks of clean data, then act.",
  },

  // ── COIB ─────────────────────────────────────────────────────────────────
  COIB: {
    leaseId: "COIB",
    leaseName: "COI - B -",
    primaryAction: "Post-Workover Watch",
    priority: "Medium",
    confidence: "High",
    shortThesis:
      "Post-workover recovery confirmed in actuals: water p95 collapsed from 7,204 to 698 bbl/d (10× reduction), oil trend reversed to +14%, MAPE down from 111% to 45%. New pump is producing as expected. Continue monitoring; ongoing 'pulled water off bottom of tanks' is normal post-workover housekeeping.",
    whyItMatters:
      "April's 'do NOT shut in, new pump just went in' call held. Fresh data shows the pump is working: oil trend reversed from -46% to +14%, water economics dramatically improved, MAPE tightened materially. The pumper's daily 'pulled water off bottom of the tanks' notes (05-01 → 05-03) are routine fluid management, not new problems. Lease has moved from emergency post-workover watch to normal monitoring with positive recovery signal.",
    productionSignal:
      "Oil p50 6.9 / p95 23.4 bbl/d (81d, MAPE 45% — was 111%); trend +14% 7v30 (was -46%). Gas inactive. Water p50 30 / p95 698 bbl/d (36d, MAPE 130%) — p95 down 10× from pre-workover 7,204.",
    econSignal:
      "Now economically evaluable. Oil rate at p50 7 bbl/d × $70 × 30 = ~$14.7k/mo gross; SWD at p95 ~698 bbl/d × $1-3 × 30 = $21k-$63k/mo cost — water disposal is the dominant lever now that pump is producing. Realistically the cost band will average lower since p50 is 30 not 698.",
    recommendedActions: [
      "Re-evaluate full lease economics at ~2026-05-19 (~30d post-pump start) with EconModal — water p95 698 needs SWD pricing locked in",
      "Continue tracking new-pump oil output; current trend +14% is the positive signal",
      "Routine water transfer notes are housekeeping — not a new flag",
    ],
    riskFlags: [
      "Water p95 still 698 bbl/d (was 7,204 — improving but elevated)",
      "Water MAPE 130%",
      "Oil MAPE 45% (improving but moderate)",
      "Post-workover baseline still characterizing",
    ],
    evidence: [
      "Oil p50/p95 = 6.9 / 23.4 bbl/d (81d, MAPE 45% — was 111%); trend +14% (was -46%)",
      "Gas: 0 positive days",
      "Water p50/p95 = 30 / 698 bbl/d (36d, MAPE 130%) — p95 down 10× from earlier 7,204",
      "Notes 05-01, 05-02, 05-03: 'pulled water off bottom of the tanks' (routine housekeeping)",
      "Notes 04-12 → 04-19 (historical): belts burnt off, bottom pulled, 'new pump works great'",
    ],
    operatorTone:
      "Pump is producing, water is way down. April call held. Now run normal econ on it.",
  },

  // ── HORSEH ───────────────────────────────────────────────────────────────
  HORSEH: {
    leaseId: "HORSEH",
    leaseName: "HORSE HOLLOW",
    primaryAction: "Post-Rig Stabilization Watch",
    priority: "Medium",
    confidence: "High",
    shortThesis:
      "Restart confirmed: pumper logged 'Well back in service' 04-28, gas trend reversed to +12%, oil 7v30 lifted strongly. Continue stabilization watch through ~05-15 to characterize true post-rig steady-state.",
    whyItMatters:
      "Rig workover narrative held: well shut in 04-20, reassembled 04-25, restarted 04-27, in service 04-28. Gas at MAPE 14% remains the cleanest signal and is now trending +12% — the well is producing. Oil 7v30 lifted dramatically (the trend metric is noisy at p50 2.5, but directionally the well is recovering). Forecast is now fresh (0d). Premature shut-in call would still be wrong.",
    productionSignal:
      "Oil p50 2.5 / p95 13.9 bbl/d (69d, MAPE 29%, anomalies 8). Gas p50 22 / p95 24 mcf/d (71d, MAPE 14% — clean); trend +12% 7v30. Water sparse (9 positive days, p95 94 bbl/d).",
    econSignal:
      "Below typical optimization threshold at p50 2.5 bbl/d oil — but the +12% gas trend says the rig work was productive. Net economics defensible only after 14+ more days of clean post-rig data. If p50 holds <2 bbl/d sustained, formal shut-in evaluation warranted; if it lifts above 4, this re-enters the optimization bench.",
    recommendedActions: [
      "Continue stabilization watch through ~2026-05-15 (~3 weeks post-04-27 restart)",
      "Track gas at MAPE 14% as the clean signal — if gas holds the +12% trend, oil should follow",
      "If post-rig steady-state oil settles <2 bbl/d, formal shut-in evaluation; if >4, optimization bench",
    ],
    riskFlags: [
      "Day-7 post-restart, still characterizing",
      "Oil p50 below typical threshold",
      "Water sparse (9 positives)",
      "Trend metric noisy at low absolute oil",
    ],
    evidence: [
      "Oil p50/p95 = 2.5 / 13.9 bbl/d (69d, MAPE 29%, 8 anomalies)",
      "Gas p50/p95 = 22 / 24 mcf/d (71d, MAPE 14%); trend +12% 7v30",
      "Water sparse (9 positives, MAPE 78%)",
      "Note 04-28: 'Well back in service'",
      "Note 04-27: 'Got well started this morning around 11.30'",
      "Notes 04-20 → 04-25: rig workover sequence",
    ],
    operatorTone:
      "Rig work is paying off in the gas trend. Give it three weeks then call it — shut in, monitor, or optimize.",
  },

  // ── MCMILLINI1 ──────────────────────────────────────────────────────────
  MCMILLINI1: {
    leaseId: "MCMILLINI1",
    leaseName: "MCMILLINI 1",
    primaryAction: "Monitor",
    priority: "Low",
    confidence: "Medium",
    shortThesis:
      "Stable low-rate producer (~9 bbl/d oil) with very actively-tracked water gauges (63 daily 'NN wtr' notes). No active issue; clean signal; just keep it on the bench.",
    whyItMatters:
      "63 notes look operationally rich at first glance, but they're all daily water-tank gauge readings ('80 wtr', '100 wtr', etc.) — the pumper is tracking the tank height, not flagging an event. Oil model is reasonable (MAPE 19%, p50 9.2). Water dataset is sparse on the production side (1 positive day) because the operator records gauges via notes rather than measured flow. Lease is healthy; no anomaly to act on.",
    productionSignal:
      "Oil p50 9.2 / p95 12.7 bbl/d (55d, MAPE 19%, trend -4% 7v30 — modest, within model). Gas inactive. Water sparse production data (1 positive day at 100 bbl); operator-tracked via daily gauges in notes.",
    econSignal:
      "Stable low-rate producer at default deck. No active dollar lever; below typical optimization threshold but not a shut-in candidate. Track quarterly for material shifts.",
    recommendedActions: [
      "No action — stable producer; keep on monthly review cadence",
      "If notes shift from gauge readings to event language, escalate",
      "Re-evaluate when oil p50 moves >15% in either direction",
    ],
    riskFlags: [
      "Below typical optimization threshold",
      "Water tracked via gauges, not measured flow",
      "Recent oil trend -4% (within model noise)",
    ],
    evidence: [
      "Oil p50/p95 = 9.2 / 12.7 bbl/d (55d, MAPE 19%)",
      "Gas: 0 positive days",
      "Water: 1 positive production day; 63 pumper-recorded gauge notes",
      "Notes 05-01 → 05-03: '80 wtr' (daily water-tank gauge readings, not events)",
    ],
    operatorTone:
      "Pumper is doing his job — daily gauges, no events. Steady producer, leave it alone.",
  },

  // ── MCSU44 ──────────────────────────────────────────────────────────────
  MCSU44: {
    leaseId: "MCSU44",
    leaseName: "MCSU 44 (INJ)",
    primaryAction: "Monitor",
    priority: "Low",
    confidence: "Low",
    shortThesis:
      "Single 'Shut in' note on 05-01 with no production data returned — confirm whether shut-in is planned or anomalous before taking action.",
    whyItMatters:
      "MCSU44 returned no Oil/Gas/Water actuals from overlay_staged. The only operational signal is one note: 'Shut in' (05-01, INJ tank). One isolated note isn't enough to diagnose intent — could be planned maintenance, could be unplanned. No production-side economics to anchor a call.",
    productionSignal:
      "No production data in fresh overlay payload (Oil/Gas/Water all empty for this lease — likely an injection well, not a producer).",
    econSignal:
      "Cannot size dollars — no production series. If this is an SWD/injection well, the relevant economics are uptime and disposal contract, not production rate.",
    recommendedActions: [
      "Confirm with field whether 05-01 'Shut in' was planned",
      "Verify lease is correctly classified as injection vs producer in the bundle",
      "If producer with planned shut-in, set expected return date; if injection, capture disposal-contract context",
    ],
    riskFlags: [
      "No production data",
      "Single ambiguous note",
      "Likely injection well (asset-type confirmation needed)",
    ],
    evidence: [
      "No Oil/Gas/Water actual data in fresh response",
      "Note 05-01 (INJ): 'Shut in'",
    ],
    operatorTone:
      "One note, no production data. Find out if shut-in was planned before deciding anything.",
  },

  // ── MCSU503 ─────────────────────────────────────────────────────────────
  MCSU503: {
    leaseId: "MCSU503",
    leaseName: "MCSU 503 (INJ)",
    primaryAction: "Investigate Decline",
    priority: "Medium",
    confidence: "High",
    shortThesis:
      "Sustained shut-in with 1400 psi backside communication — five consecutive notes (03-04 through 03-08) plus earlier shut-ins on 02-25/26. Mechanical issue, not paperwork.",
    whyItMatters:
      "Pumper logged 'Shut in. 1400 psi on backside' five times in a row in early March, with two earlier 'Shut in' notes in late February. Backside communication at 1400 psi is a serious mechanical signal — almost certainly tubing/casing leak or packer failure. The fact that it's persisted across multiple notes without a 'back on' resolution suggests the well is still down. Production data is empty (likely SWD/injection role), so this is operational, not economic.",
    productionSignal:
      "No production series in overlay. Operational classification: injection/SWD well based on tank_id 'INJ' in notes.",
    econSignal:
      "Disposal-side cost. If MCSU503 is a primary injector for a producing pad, its sustained shut-in may be forcing SWD volume onto another well (incurring rerouting costs and creating attribution noise on producers). Locate dependent producers before sizing.",
    recommendedActions: [
      "Workover diagnostic on MCSU503 — 1400 psi backside is consistent with tubing/casing leak or packer failure",
      "Map dependent producers — which leases route SWD through MCSU503?",
      "Document operational status (still down vs. resolved) since 03-08 was the last note",
    ],
    riskFlags: [
      "1400 psi backside communication (5 consecutive days)",
      "Sustained shut-in",
      "No production series",
      "Likely tubing/casing or packer failure",
    ],
    evidence: [
      "Notes 03-04 → 03-08: 'Shut in. 1400 psi on backside' (5 consecutive days)",
      "Notes 02-25, 02-26: 'Shut in' (earlier)",
      "No Oil/Gas/Water data — likely injection well",
    ],
    operatorTone:
      "1400 psi on the backside for five days. That is tubing or packer. Pull a workover diagnostic.",
  },

  // ── MCSU5101 ────────────────────────────────────────────────────────────
  MCSU5101: {
    leaseId: "MCSU5101",
    leaseName: "MCSU 5101 (INJ)",
    primaryAction: "Investigate Decline",
    priority: "Medium",
    confidence: "Medium",
    shortThesis:
      "Backside communication at 1300 psi (02-17 and 02-19) and again on 03-06; previously came back on production 02-12 — recurring backside-comm problem, similar pattern to MCSU503.",
    whyItMatters:
      "Four notes tell a recurring story: 'Back on production' 02-12, then 'Shut in. Back side communication. 1300 psi' 02-17 and 02-19, then again 'Shut in. Pressure back side' 03-06. The well comes on briefly, then shuts in on backside pressure repeatedly. Same family of mechanical problem as MCSU503 (likely shared infrastructure or chronic packer/tubing issue).",
    productionSignal:
      "No production series in overlay. INJ tank_id confirms injection-well classification.",
    econSignal:
      "Disposal exposure if dependent producers are using MCSU5101 — recurring shut-ins force rerouting. Cluster diagnostic with MCSU503 may identify a shared root cause.",
    recommendedActions: [
      "Cluster MCSU5101 with MCSU503 in workover triage — both show backside-comm shut-ins",
      "Pull current operational status; last note was 03-06",
      "Identify producers dependent on these injectors before sizing the cluster cost",
    ],
    riskFlags: [
      "Recurring backside communication (1300 psi)",
      "Three shut-in events in 3 weeks",
      "Cluster pattern with MCSU503",
      "No production series",
    ],
    evidence: [
      "Note 03-06: 'Shut in. Pressure back side'",
      "Notes 02-17, 02-19: 'Shut in. Back side communication. 1300 psi'",
      "Note 02-12: 'Back on production' (last positive status)",
    ],
    operatorTone:
      "Same backside story as 503. Diagnose them together — likely same root cause.",
  },

  // ── RT111 ───────────────────────────────────────────────────────────────
  RT111: {
    leaseId: "RT111",
    leaseName: "RT 111 (INJ)",
    primaryAction: "Investigate Decline",
    priority: "High",
    confidence: "High",
    shortThesis:
      "Tubing leak shut-in since 03-09-2026 — well has been down for ~8 weeks per pumper notes (55 daily entries with the same 'Shut in hole in tubing' text). Workover decision is overdue.",
    whyItMatters:
      "55 consecutive daily notes from 03-09 through today (05-03) all log the same status: 'Shut in hole in tubing 3-9-2026'. That's nearly two months of zero production while the pumper has been documenting the same problem every day. This isn't a 'we're tracking it' situation — it's a deferred workover. Decision is binary: pull a workover and put RT111 back online, or formally classify it as offline-permanent and stop documenting.",
    productionSignal:
      "No production series — well has been shut-in since 03-09. Pre-shut-in baseline not in the overlay window.",
    econSignal:
      "Cost-of-deferral is real but unsized without pre-shut-in production. If RT111 was a meaningful producer, ~8 weeks × pre-shut-in daily rate × oil price quantifies the gap. Workover capex must beat the deferred-revenue NPV plus equipment risk.",
    recommendedActions: [
      "Workover decision now — 8 weeks of identical pumper notes is a stuck ticket, not active monitoring",
      "Pull pre-03-09 production data to size the deferred-revenue cost",
      "If keeping offline, formally retire from the daily-gauge cycle to free pumper time",
    ],
    riskFlags: [
      "Tubing leak shut-in 8+ weeks (since 2026-03-09)",
      "55 identical daily notes — stuck workover decision",
      "No pre-shut-in production data in current window",
      "Pumper time being spent on dead reporting",
    ],
    evidence: [
      "Notes 03-09 → 05-03: 'Shut in hole in tubing 3-9-2026' (55 consecutive identical daily entries)",
      "No Oil/Gas/Water production data in overlay",
    ],
    operatorTone:
      "Eight weeks of the same note is a decision that has not been made. Pull the workover or retire the lease.",
  },

  // ── RT123 ───────────────────────────────────────────────────────────────
  RT123: {
    leaseId: "RT123",
    leaseName: "RT 123 (INJ)",
    primaryAction: "Shut-in Candidate",
    priority: "Low",
    confidence: "High",
    shortThesis:
      "Tubing leak dating back to 7/10/24 — 91 consecutive identical notes spanning ~10 months. This well has effectively been retired without paperwork; formalize it.",
    whyItMatters:
      "The pumper has been logging 'Hole in tubing 7/10/24' every single day for nearly a year. Either the leak was repaired and someone forgot to update the note template, or the well has been quietly offline for 10 months. Either way, the operational cost of the daily false-flag note is real (pumper time, ticket noise) while the well contributes nothing. Compare RT123 to RT111: RT111 is a recent (8 weeks) deferred decision; RT123 is a long-stale entry.",
    productionSignal:
      "No production series — well has been documented as 'hole in tubing' for ~10 months. Effectively offline.",
    econSignal:
      "Asset-light decision: formalize shut-in/retirement to recover pumper time and clean up reporting. Workover economics on a 10-month-down well are likely uneconomic without prior production context.",
    recommendedActions: [
      "Verify with field whether the 7/10/24 leak is still active or whether the note is stale documentation",
      "If still active, workover decision is overdue — quantify pre-leak production from archive data",
      "If the leak was actually repaired, update the note template; the 91-entry stuck status is wasting pumper attention",
    ],
    riskFlags: [
      "Tubing leak documented since 2024-07-10 (~10 months)",
      "91 identical notes — likely stale paperwork",
      "Reporting noise eating pumper time",
      "Indeterminate operational status",
    ],
    evidence: [
      "Notes 04-26 → 05-03 (sample of 91): 'Hole in tubing 7/10/24'",
      "No Oil/Gas/Water production data in overlay",
    ],
    operatorTone:
      "Ten months of the same note. This well is effectively retired — make it official.",
  },
};

/* ───────────────────────────────────────────────────────────────────────────
 * LCS_DECISION_CARDS — structured decision-engine cards.
 *
 * Each card is the authoritative right-rail render. Profiles above retain the
 * prose narrative for the collapsed Evidence/Detail drawer, but the card is
 * what an operator sees first.
 *
 * Schema:
 *   status            short pill, e.g. "OPTIMIZATION CANDIDATE", "INVESTIGATE"
 *   primaryMove       short action title, max ~50 chars
 *   decisionLine      single sentence, what changed and what to do
 *   changeSummary[]   { label, before, after, verdict }  (compact chips)
 *   productionSignals[] { stream, value, signal, caution? }
 *   moneyLever        { label, upside, caveat }
 *   nextActions[]     2-3 imperative bullets
 *   cautionFlags[]    short tags
 *   evidence[]        1-3 punchy bullets (full prose stays in profile)
 * ─────────────────────────────────────────────────────────────────────── */
export const LCS_DECISION_CARDS = {
  CARTERBF: {
    status: "RESOLVED → OPTIMIZATION CANDIDATE",
    primaryMove: "Review water + chemical program",
    decisionLine:
      "March gas-handling issue appears resolved; this is now a stable producer and the live lever is water/chemical cost control.",
    changeSummary: [
      { label: "Gas MAPE",    before: "65%",       after: "12%",        verdict: "improved" },
      { label: "Gas trend",   before: "-22%",      after: "+1%",        verdict: "stabilized" },
      { label: "Water p95",   before: "393 bbl/d", after: "217 bbl/d",  verdict: "improved" },
      { label: "Forecast",    before: "35d stale", after: "0d fresh",   verdict: "refreshed" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 27 bbl/d",  signal: "stable",   caution: "-5% 7v30" },
      { stream: "Gas",   value: "p50 41 mcf/d",  signal: "clean",    caution: "MAPE 12%" },
      { stream: "Water", value: "p95 217 bbl/d", signal: "volatile", caution: "MAPE 300%" },
    ],
    moneyLever: {
      label: "Water disposal + chemical program",
      upside: "$3k–$9k/mo (directional)",
      caveat: "Water model still noisy — stage changes, don't commit",
    },
    nextActions: [
      "Confirm separator/back-pressure fix is permanent",
      "Pull 60d water-disposal tickets / SWD terms",
      "Audit chemical program against current oil rate",
    ],
    cautionFlags: ["Gas model clean", "Water model noisy", "Forecast fresh", "Prior surface issue resolved"],
    evidence: [
      "03-07 note: gas line/two-phase/knockout pressure",
      "Gas MAPE 65% → 12%, trend -22% → +1%",
      "Water p95 393 → 217 bbl/d",
    ],
  },

  CHADR288: {
    status: "INVESTIGATE",
    primaryMove: "Repair meter today",
    decisionLine:
      "Three consecutive 'Meter is asleep' notes (05-01/02/03) — recent oil/gas signals are unreliable until measurement is restored.",
    changeSummary: [
      { label: "Active issue", before: "haul paperwork", after: "meter asleep ×3d", verdict: "escalated" },
      { label: "Oil MAPE",     before: "76%",            after: "8%",                verdict: "improved" },
      { label: "Gas trend",    before: "-6%",            after: "+14%",              verdict: "but suspect" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 9.2 bbl/d",   signal: "suspect", caution: "meter intermittent" },
      { stream: "Gas",   value: "p50 32 mcf/d",    signal: "suspect", caution: "+14% may be artifact" },
      { stream: "Water", value: "sparse (4 days)", signal: "n/a",     caution: null },
    ],
    moneyLever: {
      label: "Restore measurement first",
      upside: "Cannot size — data unreliable",
      caveat: "If meter is the only issue, gas +14% trend is real upside",
    },
    nextActions: [
      "Dispatch tech to inspect/repair meter today",
      "After 7 clean days, re-evaluate gas +14% trend",
      "Hold optimization until measurement restored",
    ],
    cautionFlags: ["Meter asleep ×3 days", "Recent rates may be artifacts", "Forecast fresh"],
    evidence: [
      "Notes 05-01, 05-02, 05-03: 'Meter is asleep'",
      "Earlier notes: routine haul tickets",
    ],
  },

  GILLUMA: {
    status: "INVESTIGATE",
    primaryMove: "Escalate 2R to integrity review",
    decisionLine:
      "2R flow-line leak repaired three consecutive days (04-27/28/29) — pattern, not repair. Watch for a fourth event before sizing optimization.",
    changeSummary: [
      { label: "Leak events", before: "2 days",   after: "3 days",   verdict: "recurring" },
      { label: "Water trend", before: "+22%",     after: "+32%",     verdict: "elevated" },
      { label: "Water p95",   before: "120 bbl/d", after: "140 bbl/d", verdict: "rising" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 9.3 bbl/d",  signal: "tight",    caution: "MAPE 8%, but pre-event" },
      { stream: "Gas",   value: "inactive",       signal: "n/a",      caution: null },
      { stream: "Water", value: "p95 140 bbl/d",  signal: "volatile", caution: "MAPE 89%, trend +32%" },
    ],
    moneyLever: {
      label: "Workover decision",
      upside: "$1.1k–$3.3k/mo avoided loss if line holds",
      caveat: "Three leaks in 72h is workover territory, not optimization",
    },
    nextActions: [
      "Escalate 2R to integrity review",
      "Watch for 4th leak event over next 5–7 days",
      "Hold optimization spend until 7 leak-free days",
    ],
    cautionFlags: ["2R flow-line leaks ×3 days", "Water trend +32% post-event", "Pre-event oil model", "Workover risk if pattern continues"],
    evidence: [
      "Notes 04-27, 04-28, 04-29: '2R off due to flow line leak. Back on'",
      "Forecast fresh (refreshed today)",
    ],
  },

  NORTHW: {
    status: "INVESTIGATE",
    primaryMove: "Pull #2 belt assembly — mechanical, not weather",
    decisionLine:
      "Belt failures on Wickham #2 now recurring without rain (04-30 → 05-03, four days) — sheave/alignment issue, not weather.",
    changeSummary: [
      { label: "Belt events", before: "rain 04-18/19", after: "no-rain 04-30→05-03", verdict: "mechanical" },
      { label: "Oil trend",   before: "-1%",            after: "-24%",                verdict: "downtime-driven" },
      { label: "Water trend", before: "+22%",           after: "-76%",                verdict: "well off" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 56 bbl/d",  signal: "leaking",  caution: "trend -24%, downtime" },
      { stream: "Gas",   value: "inactive",      signal: "n/a",      caution: null },
      { stream: "Water", value: "p95 274 bbl/d", signal: "volatile", caution: "MAPE 164%, attribution" },
    ],
    moneyLever: {
      label: "Restore Wickham #2 uptime",
      upside: "$11.7k–$29.4k/mo cost avoided",
      caveat: "Each restored day at p50 56 bbl/d × $70 ≈ $3.9k",
    },
    nextActions: [
      "Pull Wickham #2 belt assembly + inspect drive train",
      "Reconcile Tank 01 with Wickham #3",
      "Once stable 7 days, return to optimization bench",
    ],
    cautionFlags: ["Belts burn #2 daily ×4", "Oil trend -24%", "Tank 01 attribution caveat"],
    evidence: [
      "Notes 04-30 → 05-03: 'Belts burn #2' (no rain)",
      "Notes 04-18/19: 'Belts burnt off due to rain' (earlier)",
      "Note 02-08: 'Tank 01 carried over from Wickham # 3'",
    ],
  },

  CRICKETSU: {
    status: "DATA QUALITY (resolving)",
    primaryMove: "Verify weekend SWD switchback",
    decisionLine:
      "Injection line repaired 04-30 with planned weekend switch back to #6 — surface emergency unwinding; +79% spike has corrected to -2%.",
    changeSummary: [
      { label: "Surface state", before: "active mess", after: "repaired 04-30", verdict: "resolving" },
      { label: "Oil trend",     before: "+79%",        after: "-2%",            verdict: "spike unwound" },
      { label: "Water trend",   before: "+37%",        after: "-37%",           verdict: "settling" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 11.7 bbl/d", signal: "settling", caution: "MAPE 28%" },
      { stream: "Gas",   value: "inactive",       signal: "n/a",      caution: null },
      { stream: "Water", value: "p95 67 bbl/d",   signal: "settling", caution: "MAPE 182%" },
    ],
    moneyLever: {
      label: "Wait for clean window",
      upside: "Returns to ~12 bbl/d optimization candidate",
      caveat: "Water MAPE 182% — don't size SWD precisely yet",
    },
    nextActions: [
      "Confirm SWD switched back to #6 over weekend",
      "Hold optimization 14 days post-switchback",
      "Re-evaluate at +14d clean reads",
    ],
    cautionFlags: ["Repair pending verification", "Water MAPE 182%", "Recent attribution reset"],
    evidence: [
      "Note 04-30: 'Injection line repaired. Switch back to #6 weekend'",
      "Note 04-15 (historical): 'PULL WATER OFF OF OIL TANK' (spike cause)",
    ],
  },

  COIB: {
    status: "WATCH (recovery confirmed)",
    primaryMove: "Re-evaluate full economics at +30d",
    decisionLine:
      "Post-workover recovery confirmed: water p95 collapsed 7,204 → 698 bbl/d (10×), oil trend reversed -46% → +14%, MAPE 111% → 45%.",
    changeSummary: [
      { label: "Water p95", before: "7,204 bbl/d", after: "698 bbl/d", verdict: "10× improved" },
      { label: "Oil trend", before: "-46%",        after: "+14%",      verdict: "reversed" },
      { label: "Oil MAPE",  before: "111%",        after: "45%",       verdict: "tightened" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 6.9 bbl/d",  signal: "recovering", caution: "MAPE 45%" },
      { stream: "Gas",   value: "inactive",       signal: "n/a",        caution: null },
      { stream: "Water", value: "p95 698 bbl/d",  signal: "improving",  caution: "MAPE 130%" },
    ],
    moneyLever: {
      label: "SWD pricing on p95 698 bbl/d",
      upside: "Lock SWD terms to size cost band",
      caveat: "Run EconModal at +30d post-pump-start (~2026-05-19)",
    },
    nextActions: [
      "Re-evaluate full economics at ~2026-05-19",
      "Track new-pump output (current trend +14%)",
      "Treat daily water-tank notes as routine",
    ],
    cautionFlags: ["Post-workover baseline", "Water MAPE 130%", "Recovery trend positive"],
    evidence: [
      "Notes 05-01-03: 'pulled water off bottom of tanks' (housekeeping)",
      "Notes 04-14-19: 'Pulled bottom off coiB new pump works great'",
    ],
  },

  HORSEH: {
    status: "WATCH (positive)",
    primaryMove: "Continue stabilization through 05-15",
    decisionLine:
      "Restart confirmed 04-28; gas trend reversed to +12% — rig work appears productive. 3-week characterization in progress.",
    changeSummary: [
      { label: "Gas trend",    before: "-2%",       after: "+12%",      verdict: "recovering" },
      { label: "Restart",      before: "day-1",     after: "day-7",     verdict: "stable so far" },
      { label: "Forecast age", before: "35d stale", after: "0d fresh",  verdict: "refreshed" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 2.5 bbl/d",   signal: "low",     caution: "below threshold" },
      { stream: "Gas",   value: "p50 22 mcf/d",    signal: "clean",   caution: "MAPE 14%, +12%" },
      { stream: "Water", value: "sparse (9 days)", signal: "n/a",     caution: null },
    ],
    moneyLever: {
      label: "Decision binary at 05-15",
      upside: "If oil >4 bbl/d post-rig, return to optimization bench",
      caveat: "If sustained <2 bbl/d, formal shut-in evaluation",
    },
    nextActions: [
      "Watch through ~2026-05-15 (~3 weeks post-restart)",
      "Track gas as clean signal (+12%)",
      "Decide shut-in vs optimize at +21d",
    ],
    cautionFlags: ["Day-7 post-restart", "Oil below typical threshold", "Gas trend positive"],
    evidence: [
      "Note 04-28: 'Well back in service'",
      "Note 04-27: 'Got well started this morning around 11.30'",
    ],
  },

  MCMILLINI1: {
    status: "WATCH (stable)",
    primaryMove: "No action — monthly review",
    decisionLine:
      "Stable low-rate producer (~9 bbl/d). 63 daily 'NN wtr' notes are pumper gauge readings, not events.",
    changeSummary: [
      { label: "Status", before: "—", after: "stable", verdict: "no change" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "p50 9.2 bbl/d",      signal: "stable", caution: "-4% 7v30 (within model)" },
      { stream: "Gas",   value: "inactive",            signal: "n/a",    caution: null },
      { stream: "Water", value: "tracked via gauges",  signal: "n/a",    caution: "1 measured day only" },
    ],
    moneyLever: {
      label: "No active lever",
      upside: "Below typical optimization threshold",
      caveat: "Quarterly review only",
    },
    nextActions: [
      "No action — stable producer",
      "Escalate if notes shift from gauges to events",
      "Re-evaluate when oil moves >15%",
    ],
    cautionFlags: ["Below optimization threshold", "Water tracked manually"],
    evidence: [
      "Notes 05-01-03: '80 wtr' (daily gauge, not events)",
      "Oil MAPE 19%",
    ],
  },

  MCSU44: {
    status: "WATCH (need context)",
    primaryMove: "Confirm shut-in is planned",
    decisionLine:
      "Single 'Shut in' note 05-01 with no production data — confirm planned vs anomalous before deciding.",
    changeSummary: [
      { label: "Operational status", before: "unknown", after: "shut in 05-01", verdict: "needs context" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "no series", signal: "n/a", caution: null },
      { stream: "Gas",   value: "no series", signal: "n/a", caution: null },
      { stream: "Water", value: "no series", signal: "n/a", caution: null },
    ],
    moneyLever: {
      label: "Cannot size — no production",
      upside: "Likely injection well, not producer",
      caveat: "Verify asset classification first",
    },
    nextActions: [
      "Confirm with field whether 05-01 'Shut in' was planned",
      "Verify lease classification (producer vs injection)",
      "If injection, capture disposal context",
    ],
    cautionFlags: ["No production series", "Single ambiguous note", "Likely injection well"],
    evidence: ["Note 05-01: 'Shut in' (INJ tank)"],
  },

  MCSU503: {
    status: "INVESTIGATE",
    primaryMove: "Workover diagnostic on 1400 psi backside",
    decisionLine:
      "Sustained shut-in with 1400 psi backside communication for 5 consecutive days — likely tubing/casing leak or packer failure.",
    changeSummary: [
      { label: "Backside pressure",  before: "—",       after: "1400 psi ×5d", verdict: "active issue" },
      { label: "Operational status", before: "unknown", after: "shut in",      verdict: "down" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "no series", signal: "n/a", caution: "injection well" },
      { stream: "Gas",   value: "no series", signal: "n/a", caution: null },
      { stream: "Water", value: "no series", signal: "n/a", caution: null },
    ],
    moneyLever: {
      label: "Cluster diagnostic with MCSU5101",
      upside: "Restored injection capacity",
      caveat: "Map dependent producers to size disposal-rerouting cost",
    },
    nextActions: [
      "Pull workover diagnostic (1400 psi backside = tubing/packer)",
      "Map dependent producers using MCSU503 for SWD",
      "Confirm operational status since 03-08",
    ],
    cautionFlags: ["1400 psi backside ×5 days", "Sustained shut-in", "No production series"],
    evidence: [
      "Notes 03-04 → 03-08: 'Shut in. 1400 psi on backside' ×5",
      "Notes 02-25, 02-26: 'Shut in' (earlier)",
    ],
  },

  MCSU5101: {
    status: "INVESTIGATE",
    primaryMove: "Cluster with MCSU503 workover",
    decisionLine:
      "Recurring backside-comm shut-ins (1300 psi) — same pattern as MCSU503; cluster diagnose for shared root cause.",
    changeSummary: [
      { label: "Recurrence", before: "—",        after: "3 shut-ins in 3 weeks", verdict: "chronic" },
      { label: "Pattern",    before: "isolated", after: "matches MCSU503",       verdict: "cluster" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "no series", signal: "n/a", caution: "injection well" },
      { stream: "Gas",   value: "no series", signal: "n/a", caution: null },
      { stream: "Water", value: "no series", signal: "n/a", caution: null },
    ],
    moneyLever: {
      label: "Shared root-cause investigation",
      upside: "Two injectors restored if pattern holds",
      caveat: "Identify dependent producers first",
    },
    nextActions: [
      "Diagnose MCSU5101 + MCSU503 together",
      "Confirm last status since 03-06",
      "Identify dependent producers",
    ],
    cautionFlags: ["Recurring 1300 psi backside", "Cluster pattern with MCSU503", "No production series"],
    evidence: [
      "Note 03-06: 'Shut in. Pressure back side'",
      "Notes 02-17, 02-19: 'Shut in. Back side communication. 1300 psi'",
      "Note 02-12: 'Back on production' (last positive)",
    ],
  },

  RT111: {
    status: "DECISION REQUIRED",
    primaryMove: "Workover or formally retire — 8 weeks down",
    decisionLine:
      "55 identical daily notes 'Shut in hole in tubing 3-9-2026' since March 9 — workover is overdue, decision is binary.",
    changeSummary: [
      { label: "Days down",    before: "—", after: "~55 days", verdict: "stuck" },
      { label: "Ticket state", before: "—", after: "stuck",    verdict: "decision pending" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "no series (offline)", signal: "n/a", caution: "shut in 03-09" },
      { stream: "Gas",   value: "no series",           signal: "n/a", caution: null },
      { stream: "Water", value: "no series",           signal: "n/a", caution: null },
    ],
    moneyLever: {
      label: "Workover capex vs deferred-revenue NPV",
      upside: "Restore production if economics work",
      caveat: "Pull pre-03-09 baseline to size deferred revenue",
    },
    nextActions: [
      "Workover decision now — 55 identical notes is stuck",
      "Pull pre-03-09 production baseline",
      "If keeping offline, retire from daily-gauge cycle",
    ],
    cautionFlags: ["8+ weeks shut-in (since 2026-03-09)", "55 identical daily notes", "Pumper time wasted"],
    evidence: [
      "Notes 03-09 → 05-03: 'Shut in hole in tubing 3-9-2026' ×55",
    ],
  },

  RT123: {
    status: "DECISION REQUIRED",
    primaryMove: "Verify status — 91 stale notes since 2024",
    decisionLine:
      "91 daily notes 'Hole in tubing 7/10/24' over ~10 months — well is effectively retired without paperwork. Formalize.",
    changeSummary: [
      { label: "Note age",      before: "—", after: "10 months",   verdict: "stale ticket" },
      { label: "Active status", before: "—", after: "unverified",  verdict: "needs check" },
    ],
    productionSignals: [
      { stream: "Oil",   value: "no series (long offline)", signal: "n/a", caution: null },
      { stream: "Gas",   value: "no series",                 signal: "n/a", caution: null },
      { stream: "Water", value: "no series",                 signal: "n/a", caution: null },
    ],
    moneyLever: {
      label: "Asset-light retirement",
      upside: "Recover pumper time + clean reporting",
      caveat: "Workover economics on 10-month-down well likely uneconomic",
    },
    nextActions: [
      "Verify with field if 7/10/24 leak is still active",
      "If active, quantify pre-leak production from archive",
      "If repaired, update note template (91-entry stuck status)",
    ],
    cautionFlags: ["Tubing leak documented since 2024-07-10", "91 identical notes", "Likely stale paperwork"],
    evidence: [
      "Notes 04-26 → 05-03 (sample of 91): 'Hole in tubing 7/10/24'",
    ],
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
 * Decision Inference Engine (data-grounded)
 *
 * Pure-JS deterministic scoring layered on top of the curated profiles.
 * Driven by per-lease numerics baked from data/lcs_decision_set_probe.json
 * (LCS_LEASE_METRICS) and the LCS_ECON_DECK heuristic. Live overlay_staged
 * payloads override the baked metrics via extractLiveMetrics().
 * ═════════════════════════════════════════════════════════════════════════ */

/* ── Per-lease numerics snapshot (baked) ─────────────────────────────────
 * Source: live overlay_staged.php fetched 2026-05-03 via
 * scripts/reprofile_lcs_decisions.mjs (no token; data/_summary.json in
 * tmp/lcs_overlay_fresh/ — gitignored). Used when no live overlay is
 * available to the cockpit (e.g. portfolio ranking before a lease is
 * selected). Live overlays override this via extractLiveMetrics().
 *
 * ageDays values reflect the live forecast metadata. GILLUMA is the only
 * lease whose forecast did not refresh in the latest backfill.
 *
 * Notes-only entries (MCSU*, RT*) carry ageDays only — they have no
 * production series; the inference engine renders priorityScore=0 and
 * relies on the curated narrative.
 * ─────────────────────────────────────────────────────────────────────── */
export const LCS_LEASE_METRICS = {
  CARTERBF: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 26.72, p95: 41.75, trend7v30: -0.0489, mape: 0.091,  anomalies: 5,  days: 194 },
      Gas:   { p50: 41,    p95: 46,    trend7v30:  0.0095, mape: 0.122,  anomalies: 11, days: 107 },
      Water: { p50: 28,    p95: 217,   trend7v30:  0.8641, mape: 3.0009, anomalies: 15, days: 91 },
    },
  },
  CHADR288: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 9.18,  p95: 13.86, trend7v30: -0.0353, mape: 0.0825, anomalies: 5,  days: 91 },
      Gas:   { p50: 32,    p95: 35,    trend7v30:  0.1384, mape: 0.0896, anomalies: 13, days: 81 },
      Water: { p50: 3,     p95: 6,     trend7v30:  null,   mape: null,   anomalies: 0,  days: 4 },
    },
  },
  GILLUMA: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 9.28,  p95: 11.6,  trend7v30: -0.0683, mape: 0.0816, anomalies: 1,  days: 92 },
      Gas:   null,
      Water: { p50: 50,    p95: 140,   trend7v30:  0.322,  mape: 0.8949, anomalies: 15, days: 41 },
    },
  },
  NORTHW: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 55.95, p95: 68.47, trend7v30: -0.24,   mape: 0.2105, anomalies: 5,  days: 92 },
      Gas:   null,
      Water: { p50: 33,    p95: 274,   trend7v30: -0.7616, mape: 1.6393, anomalies: 22, days: 46 },
    },
  },
  CRICKETSU: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 11.69, p95: 28.39, trend7v30: -0.0196, mape: 0.2835, anomalies: 8,  days: 87 },
      Gas:   null,
      Water: { p50: 16,    p95: 67,    trend7v30: -0.3659, mape: 1.8193, anomalies: 21, days: 46 },
    },
  },
  COIB: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 6.87,  p95: 23.37, trend7v30:  0.139,  mape: 0.4459, anomalies: 6,  days: 81 },
      Gas:   null,
      Water: { p50: 30,    p95: 698,   trend7v30: -0.0656, mape: 1.295,  anomalies: 17, days: 36 },
    },
  },
  HORSEH: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 2.5,   p95: 13.89, trend7v30:  2.6602, mape: 0.2948, anomalies: 8,  days: 69 },
      Gas:   { p50: 22,    p95: 24,    trend7v30:  0.1228, mape: 0.1361, anomalies: 8,  days: 71 },
      Water: { p50: 40,    p95: 94,    trend7v30: -0.8797, mape: 0.7758, anomalies: 5,  days: 9 },
    },
  },
  MCMILLINI1: {
    ageDays: 0,
    streams: {
      Oil:   { p50: 9.2,   p95: 12.65, trend7v30: -0.0367, mape: 0.1918, anomalies: 3,  days: 55 },
      Gas:   null,
      Water: { p50: 100,   p95: 100,   trend7v30:  null,   mape: null,   anomalies: 0,  days: 1 },
    },
  },
  // Notes-only operational/SWD wells (no production series in the response).
  MCSU44:   { ageDays: 0, streams: { Oil: null, Gas: null, Water: null } },
  MCSU503:  { ageDays: 0, streams: { Oil: null, Gas: null, Water: null } },
  MCSU5101: { ageDays: 0, streams: { Oil: null, Gas: null, Water: null } },
  RT111:    { ageDays: 0, streams: { Oil: null, Gas: null, Water: null } },
  RT123:    { ageDays: 0, streams: { Oil: null, Gas: null, Water: null } },
};

/* ── Helpers ─────────────────────────────────────────────────────────── */

function _clamp01to100(x) {
  if (x == null || !Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(100, x));
}

function _round(n, step = 100) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n / step) * step);
}

function _fmt$(n) {
  if (!Number.isFinite(n) || n <= 0) return "$0";
  if (n >= 1000) return "$" + n.toLocaleString("en-US");
  return "$" + Math.round(n);
}

/**
 * Extract numerics from a live overlay_staged payload (same shape as the
 * probe). Returns null if the payload doesn't carry per-stream pct.
 */
export function extractLiveMetrics(rawOverlay) {
  if (!rawOverlay || typeof rawOverlay !== "object") return null;
  const meta = rawOverlay.meta || {};
  const streams = rawOverlay.streams || {};
  const ageDays = Number(meta.ageDays) || null;
  const out = { ageDays, streams: {} };
  let anyStream = false;
  for (const name of ["Oil", "Gas", "Water"]) {
    const s = streams[name];
    if (!s || !s.actual_pct || !s.actual_pct.n) {
      out.streams[name] = null;
      continue;
    }
    anyStream = true;
    out.streams[name] = {
      p50: Number(s.actual_pct.p50) || 0,
      p95: Number(s.actual_pct.p95) || 0,
      trend7v30: s.trend_pct_7v30 == null ? null : Number(s.trend_pct_7v30),
      mape: Number(s.train_mape) || 0,
      anomalies: Number(s.anomaly_count) || 0,
      days: Number(s.actual_pct.n) || 0,
    };
  }
  return anyStream ? out : null;
}

/* ── Scoring components ──────────────────────────────────────────────── */

function _productionDeviation(metrics) {
  // |trend_pct_7v30| × 100, max across active streams. A 50% drift maxes it.
  let best = 0;
  for (const name of ["Oil", "Gas", "Water"]) {
    const s = metrics.streams[name];
    if (!s || s.trend7v30 == null) continue;
    const v = Math.abs(s.trend7v30) * 100;
    if (v > best) best = v;
  }
  // Scale so 50% → 100; cap at 100.
  return _clamp01to100(best * 2);
}

function _economicImpactPotential(metrics) {
  // Monthly oil + gas revenue magnitude. $50k/mo → 100.
  const oil = metrics.streams.Oil;
  const gas = metrics.streams.Gas;
  const monthlyOil = (oil?.p50 || 0) * LCS_ECON_DECK.oilPricePerBbl * LCS_ECON_DECK.daysPerMonth;
  const monthlyGas = (gas?.p50 || 0) * LCS_ECON_DECK.gasPricePerMcf * LCS_ECON_DECK.daysPerMonth;
  const total = monthlyOil + monthlyGas;
  return _clamp01to100((total / 50000) * 100);
}

function _anomalyIntensity(metrics) {
  // Avg anomaly_count / days across active streams × 100.
  let totalAnom = 0;
  let totalDays = 0;
  for (const name of ["Oil", "Gas", "Water"]) {
    const s = metrics.streams[name];
    if (!s || !s.days) continue;
    totalAnom += s.anomalies || 0;
    totalDays += s.days;
  }
  if (!totalDays) return 0;
  return _clamp01to100((totalAnom / totalDays) * 100);
}

function _avgMape(metrics) {
  let sum = 0;
  let n = 0;
  for (const name of ["Oil", "Gas", "Water"]) {
    const s = metrics.streams[name];
    if (!s || s.mape == null) continue;
    sum += Math.min(s.mape, 3); // cap individual MAPE at 300% for averaging
    n += 1;
  }
  return n ? sum / n : 0;
}

function _dataConfidenceWeight(metrics) {
  // Inverse of data quality — bad data → HIGH weight (needs attention).
  // Components: avg MAPE penalty (0-100) and freshness penalty (0/50/100).
  const mapePenalty = _clamp01to100(_avgMape(metrics) * 100);
  const age = metrics.ageDays ?? 999;
  const freshnessPenalty = age <= 7 ? 0 : age <= 30 ? 50 : 100;
  return (mapePenalty + freshnessPenalty) / 2;
}

function _computePriorityScore(metrics) {
  const productionDeviation     = _productionDeviation(metrics);
  const economicImpactPotential = _economicImpactPotential(metrics);
  const anomalyIntensity        = _anomalyIntensity(metrics);
  const dataConfidenceWeight    = _dataConfidenceWeight(metrics);
  const score =
    productionDeviation     * 0.35 +
    economicImpactPotential * 0.30 +
    anomalyIntensity        * 0.20 +
    dataConfidenceWeight    * 0.15;
  return {
    score: Math.round(_clamp01to100(score)),
    components: {
      productionDeviation:     Math.round(productionDeviation),
      economicImpactPotential: Math.round(economicImpactPotential),
      anomalyIntensity:        Math.round(anomalyIntensity),
      dataConfidenceWeight:    Math.round(dataConfidenceWeight),
    },
  };
}

function _priorityLabel(score) {
  if (score >= 80) return "High";
  if (score >= 50) return "Medium";
  return "Low";
}

/* ── Economic impact estimate ────────────────────────────────────────── */

function _computeEconomicImpact(metrics, primaryAction) {
  const oil = metrics.streams.Oil;
  const gas = metrics.streams.Gas;
  const water = metrics.streams.Water;
  const monthlyOil  = (oil?.p50 || 0) * LCS_ECON_DECK.oilPricePerBbl * LCS_ECON_DECK.daysPerMonth;
  const monthlyGas  = (gas?.p50 || 0) * LCS_ECON_DECK.gasPricePerMcf * LCS_ECON_DECK.daysPerMonth;
  const monthlyHC   = monthlyOil + monthlyGas;
  const waterCostLo = (water?.p95 || water?.p50 || 0) * LCS_ECON_DECK.waterDisposalLow  * LCS_ECON_DECK.daysPerMonth;
  const waterCostHi = (water?.p95 || water?.p50 || 0) * LCS_ECON_DECK.waterDisposalHigh * LCS_ECON_DECK.daysPerMonth;
  const avgMape     = _avgMape(metrics);
  const action      = String(primaryAction || "");

  // Action-specific branches run BEFORE the generic data-quality fallback,
  // because the action label already encodes the operator's diagnosis.

  // Gas-handling investigation → upside on gas leg, sized off realized gas
  if (/Investigate Gas Handling/i.test(action)) {
    return {
      type: "Upside",
      range: `${_fmt$(_round(monthlyGas * 0.10))}–${_fmt$(_round(monthlyGas * 0.30))} / month`,
      confidence: "Medium",
    };
  }

  // Optimize Production → 5-15% of monthly HC revenue
  if (/Optimize/i.test(action)) {
    return {
      type: "Upside",
      range: `${_fmt$(_round(monthlyHC * 0.05))}–${_fmt$(_round(monthlyHC * 0.15))} / month`,
      confidence: avgMape < 0.3 ? "High" : "Medium",
    };
  }

  // Verify Line Integrity → avoided downside if line holds, sized off oil
  if (/Verify Line/i.test(action)) {
    return {
      type: "Upside",
      range: `${_fmt$(_round(monthlyOil * 0.05))}–${_fmt$(_round(monthlyOil * 0.15))} / month (avoided loss)`,
      confidence: "Medium",
    };
  }

  // Investigate Decline → potential downside on oil. If no oil baseline
  // (notes-only / shut-in well), surface that honestly instead of "$0".
  if (/Investigate Decline/i.test(action)) {
    if (monthlyOil <= 0) {
      return {
        type: "Unclear",
        range: "Impact unclear — no production baseline (well likely shut-in)",
        confidence: "Low",
      };
    }
    return {
      type: "Cost Risk",
      range: `${_fmt$(_round(monthlyOil * 0.10))}–${_fmt$(_round(monthlyOil * 0.25))} / month`,
      confidence: avgMape < 1.5 ? "Medium" : "Low",
    };
  }

  // Water Burden Review → SWD cost band on p95
  if (/Water Burden|Review LOE/i.test(action)) {
    return {
      type: "Cost Risk",
      range: `${_fmt$(_round(waterCostLo))}–${_fmt$(_round(waterCostHi))} / month`,
      confidence: avgMape < 1.5 ? "Medium" : "Low",
    };
  }

  // Shut-in candidate → fixed-cost recovery range
  if (/Shut-in/i.test(action)) {
    return {
      type: "Cost Risk",
      range: "$2,000–$5,000 / month (LOE recovery on shut-in)",
      confidence: "Low",
    };
  }

  // Post-Workover / Post-Rig → window contaminated, can't estimate
  if (/Post-Workover|Post-Rig|Workover|Rig/i.test(action)) {
    return {
      type: "Unclear",
      range: "Impact unclear — post-workover re-baseline window",
      confidence: "Low",
    };
  }

  // Data Quality → surface-side noise, can't estimate
  if (/Data Quality/i.test(action)) {
    return {
      type: "Unclear",
      range: "Impact unclear — surface-side data contamination",
      confidence: "Low",
    };
  }

  // Monitor → no active dollar lever
  if (/Monitor/i.test(action)) {
    return {
      type: "Unclear",
      range: "No active dollar lever — monitor only",
      confidence: "Medium",
    };
  }

  // Generic data-quality fallback for unrecognized actions
  if (avgMape > 1.0) {
    return {
      type: "Unclear",
      range: "Impact unclear — data quality limits estimate",
      confidence: "Low",
    };
  }
  return {
    type: "Unclear",
    range: "Impact unclear — insufficient signal",
    confidence: "Low",
  };
}

/* ── Data freshness ──────────────────────────────────────────────────── */

function _computeDataFreshness(metrics) {
  const age = metrics.ageDays ?? 999;
  if (age <= 7) {
    return { status: "Fresh", ageDays: age, warning: "" };
  }
  if (age <= 30) {
    return {
      status: "Slightly Stale",
      ageDays: age,
      warning: `Forecast is ${age} days old — directional only`,
    };
  }
  return {
    status: "Stale",
    ageDays: age,
    warning: `Forecast is ${age} days old — treat all forward-looking conclusions as directional only`,
  };
}

/* ── Key drivers (3 short bullets pulled from signal sources) ────────── */

function _computeKeyDrivers(metrics, profile) {
  const drivers = [];
  // 1. Strongest production signal
  let prodSignal = null;
  let prodMag = 0;
  for (const name of ["Oil", "Gas", "Water"]) {
    const s = metrics.streams[name];
    if (!s || s.trend7v30 == null) continue;
    const mag = Math.abs(s.trend7v30);
    if (mag > prodMag) {
      prodMag = mag;
      const dir = s.trend7v30 < 0 ? "down" : "up";
      const pct = Math.round(Math.abs(s.trend7v30 * 100));
      prodSignal = `${name} ${dir} ${pct}% 7v30 (p50 ${Math.round(s.p50 * 10) / 10})`;
    }
  }
  if (prodSignal) drivers.push(`Production: ${prodSignal}`);

  // 2. Note signal — pull from curated evidence
  if (profile?.evidence?.length) {
    const noteRow = profile.evidence.find((e) => /note|2026-/i.test(e));
    if (noteRow) {
      const trimmed = noteRow.length > 120 ? noteRow.slice(0, 117) + "…" : noteRow;
      drivers.push(`Field note: ${trimmed.replace(/^Note[s]?\s+\d{4}-\d{2}-\d{2}:?\s*/i, "")}`);
    }
  }

  // 3. Forecast reliability
  const avgMape = _avgMape(metrics);
  const age = metrics.ageDays ?? 0;
  const reliability =
    avgMape > 1.0 ? `MAPE elevated (${Math.round(avgMape * 100)}% avg)`
    : avgMape > 0.3 ? `MAPE moderate (${Math.round(avgMape * 100)}% avg)`
    : `MAPE clean (${Math.round(avgMape * 100)}% avg)`;
  drivers.push(`Forecast reliability: ${reliability}, ${age}d stale`);

  return drivers;
}

/* ── Main inference function ─────────────────────────────────────────── */

/**
 * Compute the Decision Inference Engine output for a lease.
 *
 * Inputs:
 *   leaseId      — string
 *   metrics      — output of extractLiveMetrics() OR LCS_LEASE_METRICS[leaseId]
 *   profile      — LCS_DECISION_PROFILES[leaseId] (may be null)
 *
 * Returns the structured Decision Inference object.
 */
export function computeDecisionInference({ leaseId, metrics, profile } = {}) {
  if (!metrics) {
    return {
      leaseId,
      inferenceEngine: "Decision Inference Engine (data-grounded)",
      priorityScore: 0,
      priorityLabel: "Low",
      diagnosis: profile?.shortThesis || "",
      confidence: profile?.confidence || "Low",
      economicImpact: { type: "Unclear", range: "No metrics available", confidence: "Low" },
      dataFreshness: { status: "Stale", ageDays: 0, warning: "No forecast metadata available" },
      keyDrivers: [],
      recommendedActions: (profile?.recommendedActions || []).slice(0, 3),
    };
  }

  const { score, components } = _computePriorityScore(metrics);
  const priorityLabel = _priorityLabel(score);
  const economicImpact = _computeEconomicImpact(metrics, profile?.primaryAction);
  const dataFreshness = _computeDataFreshness(metrics);
  const keyDrivers = _computeKeyDrivers(metrics, profile);

  return {
    leaseId,
    inferenceEngine: "Decision Inference Engine (data-grounded)",
    priorityScore: score,
    priorityLabel,
    priorityComponents: components,
    diagnosis: profile?.shortThesis || "",
    confidence: profile?.confidence || "Medium",
    economicImpact,
    dataFreshness,
    keyDrivers,
    recommendedActions: (profile?.recommendedActions || []).slice(0, 3),
  };
}

/**
 * Resolve a lease's decision profile (curated narrative + inference engine).
 *
 * Priority for the narrative layer:
 *   1. fresh upstream `llm` payload (overlay_staged include_llm) — pass via `liveLlm`
 *   2. LCS_DECISION_PROFILES[leaseId]
 *   3. null — caller falls back to deterministic inferLeaseCommentary
 *
 * The inference layer (priorityScore, economicImpact, dataFreshness,
 * keyDrivers) is computed deterministically on top of either:
 *   - live overlay numerics via `liveOverlay` (preferred), OR
 *   - baked LCS_LEASE_METRICS[leaseId] (fallback)
 *
 * Returns null only when there is no curated profile AND no LLM payload.
 */
export function resolveDecisionProfile({ leaseId, liveLlm, liveOverlay } = {}) {
  // Hand-crafted cards always win. Synthesized cards (generated from live
  // overlay_staged at build time) provide coverage for every lease in the
  // canonical bundle. Generated cards source: src/data/lcsGeneratedCards.json
  // (produced by scripts/synthesize_lcs_cards.mjs).
  const handCard = LCS_DECISION_CARDS[leaseId] || null;
  const genCard  = LCS_GENERATED_CARDS[leaseId] || null;
  const decisionCard = handCard || genCard || null;
  const cardSource = handCard ? "curated" : (genCard ? "synthesized" : null);

  // Build the legacy prose profile for the Evidence drawer + as a fallback
  // when neither a curated profile nor a card exists.
  let baseProfile;
  if (liveLlm && typeof liveLlm === "object" && liveLlm.diagnosis) {
    baseProfile = {
      leaseId,
      primaryAction: liveLlm.action_label || "Monitor",
      priority: "Medium",
      confidence: liveLlm.diagnosis_confidence || "Medium",
      shortThesis: liveLlm.diagnosis,
      whyItMatters: liveLlm.action_why || "",
      productionSignal: liveLlm.notes_synthesis || "",
      econSignal: "",
      recommendedActions: liveLlm.action_why ? [liveLlm.action_why] : [],
      riskFlags: liveLlm.risk_factors || [],
      evidence: [],
      operatorTone: "",
      _source: "llm",
    };
  } else if (LCS_DECISION_PROFILES[leaseId]) {
    baseProfile = { ...LCS_DECISION_PROFILES[leaseId], _source: "lcs_decision_profiles" };
  } else if (decisionCard) {
    // No curated prose profile, but we have a structured card — synthesize a
    // minimal wrapper so the right-rail header (priority/confidence) and the
    // legacy fallback all keep working.
    baseProfile = {
      leaseId,
      leaseName: leaseId,
      primaryAction: decisionCard.primaryMove || "Monitor",
      priority: decisionCard.priority || "Low",
      confidence: decisionCard.confidence || "Medium",
      shortThesis: decisionCard.decisionLine || "",
      whyItMatters: "",
      productionSignal: "",
      econSignal: "",
      recommendedActions: Array.isArray(decisionCard.nextActions) ? decisionCard.nextActions.slice(0, 3) : [],
      riskFlags: Array.isArray(decisionCard.cautionFlags) ? decisionCard.cautionFlags.slice(0, 4) : [],
      evidence: Array.isArray(decisionCard.evidence) ? decisionCard.evidence : [],
      operatorTone: "",
      _source: cardSource === "synthesized" ? "lcs_generated_cards" : "lcs_decision_cards",
    };
  } else {
    return null;
  }

  const liveMetrics = extractLiveMetrics(liveOverlay);
  let metrics = liveMetrics || LCS_LEASE_METRICS[leaseId] || null;
  // If we have NO metrics at all but the synthesizer recorded a live ageDays
  // for this lease, synthesize a metrics-shape with just the freshness so
  // the freshness panel renders correctly (Fresh/Stale) instead of "Unknown".
  if (!metrics && typeof LCS_LIVE_AGE_DAYS[leaseId] === "number") {
    metrics = {
      ageDays: LCS_LIVE_AGE_DAYS[leaseId],
      streams: { Oil: null, Gas: null, Water: null },
    };
  }

  const inference = computeDecisionInference({ leaseId, metrics, profile: baseProfile });

  // Forward-tracking Recommendation Ledger (Proof Ledger Lite). Derived
  // deterministically from the resolved card + metrics. Outcomes are PENDING
  // — we never claim historical proof.
  const ledger = decisionCard
    ? deriveLedger({
        leaseId,
        leaseName: baseProfile.leaseName || leaseId,
        card: decisionCard,
        metrics,
      })
    : null;

  return {
    ...baseProfile,
    inference,
    decisionCard,
    ledger,
    _cardSource: cardSource,
    _metricsSource: liveMetrics ? "live_overlay" : (metrics ? "baked_metrics" : "none"),
  };
}
